///
/// EarthBrowser
///
/// Created by Matt Giger on 8/25/2024
/// Copyright (c) 2024 Geodata Labs
///

import { DataLoader } from "@/earthbrowser/utils/loaders/DataLoader"
import { type Font, parse } from "@/earthbrowser/libs/opentype.js"

export class FontCache {
	private static _cache = new Map<string, Font>()
	private static _loadingPromises = new Map<string, Promise<Font>>()

	public static clear(): void {
		FontCache._cache.clear()
		FontCache._loadingPromises.clear()
	}

	public static async load(fontURL: string): Promise<Font> {
		// Check if font is already loaded
		const cachedFont = FontCache._cache.get(fontURL)
		if(cachedFont !== undefined) {
			return cachedFont
		}

		// Check if font is currently being loaded
		const loadingPromise = FontCache._loadingPromises.get(fontURL)
		if(loadingPromise !== undefined) {
			return loadingPromise
		}

		// Start loading the font
		const promise = FontCache._loadFont(fontURL)
		FontCache._loadingPromises.set(fontURL, promise)

		try {
			const font = await promise
			FontCache._cache.set(fontURL, font)
			return font
		} finally {
			// Remove from loading promises once complete (success or failure)
			FontCache._loadingPromises.delete(fontURL)
		}
	}

	private static async _loadFont(fontURL: string): Promise<Font> {
		const loader = new DataLoader(fontURL)
		const data = await loader.load()
		if(data) {
			const font = parse(data, {}) as Font
			return font
		}
		throw new Error(`Failed to load font: ${fontURL}`)
	}
}